import * as React from "react"

import { cn } from "@/lib/utils"
import { INPUT_LIMITS, safeTruncate } from "@/lib/input-limits"

/**
 * Shared Input component.
 *
 * P0-3 hardening: applies a default `maxLength` (200 chars) at the
 * HTML attribute layer AND a defensive truncation in onChange so that
 * massive pastes never reach downstream state setters / re-render loops.
 *
 * To override, pass explicit `maxLength={NUMBER}` to the component.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, maxLength, onChange, ...props }, ref) => {
    const effectiveMax =
      typeof maxLength === "number" && maxLength > 0
        ? maxLength
        : INPUT_LIMITS.short;

    const guardedOnChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
          // Defensive truncation: even if maxLength is bypassed (paste edge cases,
          // programmatic value assignment), enforce the cap before the handler runs.
          const v = e.target.value;
          if (typeof v === "string" && v.length > effectiveMax) {
            const truncated = safeTruncate(v, effectiveMax);
            // Mutate the event target so consumers see the safe value.
            try { (e.target as HTMLInputElement).value = truncated; } catch {}
          }
          onChange?.(e);
        } catch (err) {
          // Never let a giant input crash the form. Log and swallow.
          // eslint-disable-next-line no-console
          console.warn("[Input] onChange guard caught error", err);
        }
      },
      [onChange, effectiveMax],
    );

    // h-9 to match icon buttons and default buttons.
    return (
      <input
        type={type}
        maxLength={effectiveMax}
        onChange={guardedOnChange}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
